import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mfaEnabled: boolean;
}

export function DeleteAccountDialog({ open, onOpenChange, mfaEnabled }: DeleteAccountDialogProps) {
  const { deleteAccount } = useAuth();
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!password.trim()) {
      toast.error("Please enter your password");
      return;
    }

    if (mfaEnabled && !mfaCode.trim()) {
      toast.error("Please enter your 2FA code");
      return;
    }

    setIsDeleting(true);
    try {
      const { error } = await deleteAccount(password, mfaEnabled ? mfaCode : undefined);

      if (error) {
        if (error.message === 'MFA code required') {
          toast.error("2-step verification code is required");
        } else if (error.message === 'Invalid password') {
          toast.error("Incorrect password. Please try again.");
        } else if (error.message === 'Invalid MFA code') {
          toast.error("Invalid 2FA code. Please try again.");
        } else {
          toast.error(error.message || "Failed to delete account");
        }
        setIsDeleting(false);
        return;
      }

      toast.success("Your account has been permanently deleted");
      onOpenChange(false);
      // User will be redirected by auth state change
    } catch (error: any) {
      console.error('Delete account error:', error);
      toast.error("An unexpected error occurred");
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    if (!isDeleting) {
      setPassword("");
      setMfaCode("");
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <AlertDialogTitle>Delete Account</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-lg bg-destructive/5 p-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-2">This will permanently delete:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Your account and profile</li>
              <li>All workspaces you own</li>
              <li>All queues, tasks, and documents</li>
              <li>All files and uploaded content</li>
            </ul>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="password">Confirm your password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={isDeleting}
                className="mt-1.5"
              />
            </div>

            {mfaEnabled && (
              <div>
                <Label htmlFor="mfaCode">2-Step Verification Code</Label>
                <Input
                  id="mfaCode"
                  type="text"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  disabled={isDeleting}
                  className="mt-1.5"
                />
              </div>
            )}
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} disabled={isDeleting}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting || !password.trim() || (mfaEnabled && !mfaCode.trim())}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete My Account'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
